import { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Loader2, ImagePlus } from "lucide-react";

function resolveUrl(u) {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return `${base}${u}`;
}

function BannerSlotCard({ slot, banner, onUploaded }) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const url = resolveUrl(banner?.image_url);

  const handleUpload = async (file) => {
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      toast.error("Only image files are allowed");
      return;
    }
    if (file.size > 6 * 1024 * 1024) {
      toast.error("Image must be under 6 MB");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/banners/${slot}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Banner ${slot + 1} updated`);
      onUploaded && onUploaded(slot, data.image_url);
    } catch (err) {
      toast.error("Upload failed", {
        description: formatApiErrorDetail(err.response?.data?.detail) || err.message,
      });
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div
      className="rounded-2xl border border-white/10 bg-[#0E0E12] overflow-hidden"
      data-testid={`admin-banner-card-${slot}`}
    >
      <div className="flex items-center justify-between px-5 py-3 border-b border-white/5">
        <div>
          <div className="font-mono-label text-[#00E5FF]">
            Banner slot {slot + 1}
          </div>
          <div className="text-xs text-white/40 mt-0.5">
            JPG, PNG, WEBP or GIF · max 6 MB
          </div>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          data-testid={`admin-banner-file-${slot}`}
          onChange={(e) => handleUpload(e.target.files?.[0])}
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          data-testid={`admin-banner-upload-${slot}`}
          className="inline-flex items-center gap-2 rounded-full bg-[#E0007A] hover:bg-[#ff1493] disabled:opacity-50 text-white font-semibold text-sm px-4 py-2 glow-magenta transition-transform hover:scale-[1.02]"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          {busy ? "Uploading…" : "Replace image"}
        </button>
      </div>
      <div className="p-4">
        <div className="w-full aspect-[1391/365] rounded-xl overflow-hidden bg-black/40 border border-white/5 relative">
          {url ? (
            <img
              src={url}
              alt={`Banner slot ${slot + 1}`}
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-white/40 gap-2">
              <ImagePlus className="h-6 w-6" />
              <span className="font-mono-label">No image yet — upload one</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminBanners() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () =>
    api
      .get("/banners")
      .then(({ data }) => setBanners(data.banners || []))
      .catch(() => setBanners([]))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
  }, []);

  const handleUploaded = () => load();

  return (
    <section
      data-testid="admin-banners-panel"
      className="mt-10 rounded-2xl border border-white/10 bg-[#0E0E12] p-6"
    >
      <div>
        <div className="font-mono-label text-[#00E5FF]">Partner Banners</div>
        <h2 className="font-display text-2xl mt-1">Homepage sponsor slots</h2>
        <p className="text-sm text-white/50 mt-2 max-w-2xl">
          Two independent banner slots displayed above the footer on every public
          page. Each slot can be replaced independently without touching the
          other. Recommended image ratio 1391×365 (wide).
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-white/40" />
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-5">
          {[0, 1].map((slot) => (
            <BannerSlotCard
              key={slot}
              slot={slot}
              banner={banners.find((b) => b.slot === slot)}
              onUploaded={handleUploaded}
            />
          ))}
        </div>
      )}
    </section>
  );
}
